import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, FileText, Settings, LogOut, Plus, Edit2, Trash2, X, Save, Menu,
  MessageSquare, Star, Globe, HelpCircle, Mail, Upload, Check, AlertCircle, Loader
} from 'lucide-react'
import toast from 'react-hot-toast'
import { contentAPI } from '../../services/contentAPI'
import ImageUploader from '../../components/admin/ImageUploader'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('dashboard')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [editingItem, setEditingItem] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [formData, setFormData] = useState({})
  
  // Section-specific loading states
  const [loadingStates, setLoadingStates] = useState({
    dashboard: false,
    hero: false,
    testimonials: false,
    features: false,
    faqs: false,
    contact: false,
    saving: false
  })

  // Content state
  const [heroContent, setHeroContent] = useState(null)
  const [testimonials, setTestimonials] = useState([])
  const [features, setFeatures] = useState([])
  const [faqs, setFaqs] = useState([])
  const [contactInfo, setContactInfo] = useState(null)

  useEffect(() => {
    const isAdmin = localStorage.getItem('adminAuth')
    if (!isAdmin) {
      navigate('/admin')
    } else {
      loadAllContent()
    }
  }, [navigate])

  const setLoading = (section, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [section]: isLoading }))
  }

  const loadAllContent = async () => {
    setLoading('dashboard', true)
    try {
      const [heroRes, testimonialsRes, featuresRes, faqsRes, contactRes] = await Promise.all([
        contentAPI.getHeroContent().catch(() => ({ data: null })),
        contentAPI.getTestimonials().catch(() => ({ data: [] })),
        contentAPI.getFeatures().catch(() => ({ data: [] })),
        contentAPI.getFAQs().catch(() => ({ data: [] })),
        contentAPI.getContactInfo().catch(() => ({ data: null }))
      ])

      setHeroContent(heroRes.data)
      setTestimonials(testimonialsRes.data)
      setFeatures(featuresRes.data)
      setFaqs(faqsRes.data)
      setContactInfo(contactRes.data)
    } catch (error) {
      console.error('Error loading content:', error)
      toast.error('Failed to load some content. Please refresh the page.')
    } finally {
      setLoading('dashboard', false)
    }
  }

  const loadSectionContent = async (section) => {
    setLoading(section, true)
    try {
      switch (section) {
        case 'hero':
          const heroRes = await contentAPI.getHeroContent()
          setHeroContent(heroRes.data)
          break
        case 'testimonials':
          const testimonialsRes = await contentAPI.getTestimonials()
          setTestimonials(testimonialsRes.data)
          break
        case 'features':
          const featuresRes = await contentAPI.getFeatures()
          setFeatures(featuresRes.data)
          break
        case 'faqs':
          const faqsRes = await contentAPI.getFAQs()
          setFaqs(faqsRes.data)
          break
        case 'contact':
          const contactRes = await contentAPI.getContactInfo()
          setContactInfo(contactRes.data)
          break
      }
    } catch (error) {
      console.error(`Error loading ${section}:`, error)
      toast.error(`Failed to load ${section} content`)
    } finally {
      setLoading(section, false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('adminAuth')
    toast.success('Logged out successfully')
    navigate('/admin')
  }

  const openModal = (type, item = null) => {
    setModalType(type)
    setEditingItem(item)
    setFormData(item || getDefaultFormData(type))
    setShowModal(true)
  }

  const getDefaultFormData = (type) => {
    switch (type) {
      case 'hero':
        return {
          title: '',
          subtitle: '',
          cta_text: '',
          cta_link: '',
          secondary_cta_text: '',
          background_image: ''
        }
      case 'testimonial':
        return {
          name: '',
          role: '',
          content: '',
          image: '',
          rating: 5,
          is_active: true
        }
      case 'feature':
        return {
          title: '',
          description: '',
          icon: 'Star',
          color: 'blue',
          is_active: true,
          display_order: 0
        }
      case 'faq':
        return {
          question: '',
          answer: '',
          is_active: true,
          display_order: 0
        }
      case 'contact':
        return {
          email: '',
          phone: '',
          address: '',
          business_hours: '',
          twitter_url: '',
          linkedin_url: '',
          facebook_url: ''
        }
      default:
        return {}
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setModalType('')
    setEditingItem(null)
    setFormData({})
  }

  const handleSave = async () => {
    setLoading('saving', true)
    try {
      switch (modalType) {
        case 'hero':
          await contentAPI.updateHeroContent(formData)
          await loadSectionContent('hero')
          toast.success('Hero content updated successfully!')
          break
        
        case 'testimonial':
          if (editingItem) {
            await contentAPI.updateTestimonial(editingItem.id, formData)
            toast.success('Testimonial updated successfully!')
          } else {
            await contentAPI.createTestimonial(formData)
            toast.success('Testimonial created successfully!')
          }
          await loadSectionContent('testimonials')
          break
        
        case 'feature':
          if (editingItem) {
            await contentAPI.updateFeature(editingItem.id, formData)
            toast.success('Feature updated successfully!')
          } else {
            await contentAPI.createFeature(formData)
            toast.success('Feature created successfully!')
          }
          await loadSectionContent('features')
          break
        
        case 'faq':
          if (editingItem) {
            await contentAPI.updateFAQ(editingItem.id, formData)
            toast.success('FAQ updated successfully!')
          } else {
            await contentAPI.createFAQ(formData)
            toast.success('FAQ created successfully!')
          }
          await loadSectionContent('faqs')
          break
        
        case 'contact':
          await contentAPI.updateContactInfo(formData)
          await loadSectionContent('contact')
          toast.success('Contact info updated successfully!')
          break
      }
      closeModal()
    } catch (error) {
      console.error('Save error:', error)
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading('saving', false)
    }
  }

  const handleDelete = async (type, id) => {
    if (!deleteConfirm) {
      setDeleteConfirm({ type, id })
      return
    }

    setLoading(type + 's', true)
    try {
      switch (type) {
        case 'testimonial':
          await contentAPI.deleteTestimonial(id)
          toast.success('Testimonial deleted')
          await loadSectionContent('testimonials')
          break
        case 'feature':
          await contentAPI.deleteFeature(id)
          toast.success('Feature deleted')
          await loadSectionContent('features')
          break
        case 'faq':
          await contentAPI.deleteFAQ(id)
          toast.success('FAQ deleted')
          await loadSectionContent('faqs')
          break
      }
      setDeleteConfirm(null)
    } catch (error) {
      toast.error('Failed to delete: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(type + 's', false)
    }
  }

  const handleImageUpload = (imageData) => {
    console.log('Image uploaded:', imageData)
    toast.success('Image ready to use! URL copied to clipboard.')
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'hero', label: 'Hero Section', icon: Globe },
    { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
    { id: 'features', label: 'Features', icon: Star },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle },
    { id: 'contact', label: 'Contact Info', icon: Mail },
    { id: 'images', label: 'Image Upload', icon: Upload },
    { id: 'settings', label: 'Settings', icon: Settings },
  ]

  const LoadingSpinner = () => (
    <div className="flex justify-center items-center py-12">
      <div className="flex flex-col items-center gap-3">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        <p className="text-sm text-gray-600">Loading...</p>
      </div>
    </div>
  )

  const EmptyState = ({ icon: Icon, title, description, action }) => (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-6">{description}</p>
      {action}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`bg-gray-900 text-white w-64 fixed h-full transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 z-30`}>
        <div className="p-6 border-b border-gray-800">
          <h1 className="text-2xl font-bold">Admin Panel</h1>
          <p className="text-gray-400 text-sm mt-1">Content Management</p>
        </div>
        
        <nav className="p-4">
          {menuItems.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id)
                  if (item.id !== 'dashboard' && item.id !== 'images' && item.id !== 'settings') {
                    loadSectionContent(item.id)
                  }
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                  activeSection === item.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            )
          })}
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-gray-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-2xl font-bold text-gray-900 capitalize">{activeSection}</h2>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Dashboard Overview */}
          {activeSection === 'dashboard' && (
            <>
              {loadingStates.dashboard ? (
                <LoadingSpinner />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-gray-600 text-sm font-medium mb-2">Testimonials</h3>
                    <p className="text-3xl font-bold text-gray-900">{testimonials.length}</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-gray-600 text-sm font-medium mb-2">Features</h3>
                    <p className="text-3xl font-bold text-gray-900">{features.length}</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-gray-600 text-sm font-medium mb-2">FAQs</h3>
                    <p className="text-3xl font-bold text-gray-900">{faqs.length}</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-gray-600 text-sm font-medium mb-2">Hero Section</h3>
                    <p className="text-3xl font-bold text-gray-900">{heroContent ? '✓' : '✗'}</p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Image Upload Section */}
          {activeSection === 'images' && (
            <div className="max-w-4xl">
              <ImageUploader onUploadComplete={handleImageUpload} />
              
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4">How to Use Uploaded Images</h3>
                <ol className="space-y-3 text-blue-800">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                    <span>Upload your image using the drag-and-drop area above</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                    <span>Copy the generated Cloudinary URL</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                    <span>Paste the URL into any content field (Hero background, testimonial images, etc.)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">4</span>
                    <span>Images are automatically optimized and served via CDN for fast loading</span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* Hero Section Management */}
          {activeSection === 'hero' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Hero Section Content</h3>
                <button
                  onClick={() => openModal('hero', heroContent)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Hero
                </button>
              </div>

              {loadingStates.hero ? (
                <LoadingSpinner />
              ) : heroContent ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <p className="text-gray-900">{heroContent.title}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <p className="text-gray-900">{heroContent.subtitle}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                      <p className="text-gray-900">{heroContent.cta_text}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
                      <p className="text-gray-900">{heroContent.cta_link}</p>
                    </div>
                  </div>
                  {heroContent.background_image && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Image</label>
                      <img src={heroContent.background_image} alt="Hero background" className="w-full max-w-md rounded-lg" />
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={Globe}
                  title="No hero content configured"
                  description="Create your hero section to get started"
                  action={
                    <button
                      onClick={() => openModal('hero')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Create Hero Content
                    </button>
                  }
                />
              )}
            </div>
          )}

          {/* Testimonials Management */}
          {activeSection === 'testimonials' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Testimonials</h3>
                <button
                  onClick={() => openModal('testimonial')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Testimonial
                </button>
              </div>

              {loadingStates.testimonials ? (
                <LoadingSpinner />
              ) : testimonials.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {testimonial.image && (
                            <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                          )}
                          <div>
                            <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                            <p className="text-sm text-gray-600">{testimonial.role}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal('testimonial', testimonial)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete('testimonial', testimonial.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm line-clamp-3">{testimonial.content}</p>
                      <div className="mt-2 flex items-center gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={MessageSquare}
                  title="No testimonials yet"
                  description="Add customer testimonials to build trust"
                  action={
                    <button
                      onClick={() => openModal('testimonial')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add First Testimonial
                    </button>
                  }
                />
              )}
            </div>
          )}

          {/* Features Management */}
          {activeSection === 'features' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Features</h3>
                <button
                  onClick={() => openModal('feature')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Feature
                </button>
              </div>

              {loadingStates.features ? (
                <LoadingSpinner />
              ) : features.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {features.map((feature) => (
                    <div key={feature.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal('feature', feature)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete('feature', feature.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm">{feature.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Star}
                  title="No features yet"
                  description="Showcase your product features"
                  action={
                    <button
                      onClick={() => openModal('feature')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add First Feature
                    </button>
                  }
                />
              )}
            </div>
          )}

          {/* FAQs Management */}
          {activeSection === 'faqs' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">FAQs</h3>
                <button
                  onClick={() => openModal('faq')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add FAQ
                </button>
              </div>

              {loadingStates.faqs ? (
                <LoadingSpinner />
              ) : faqs.length > 0 ? (
                <div className="space-y-4">
                  {faqs.map((faq) => (
                    <div key={faq.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{faq.question}</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openModal('faq', faq)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete('faq', faq.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={HelpCircle}
                  title="No FAQs yet"
                  description="Answer common customer questions"
                  action={
                    <button
                      onClick={() => openModal('faq')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add First FAQ
                    </button>
                  }
                />
              )}
            </div>
          )}

          {/* Contact Info Management */}
          {activeSection === 'contact' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Contact Information</h3>
                <button
                  onClick={() => openModal('contact', contactInfo)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Contact Info
                </button>
              </div>

              {loadingStates.contact ? (
                <LoadingSpinner />
              ) : contactInfo ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <p className="text-gray-900">{contactInfo.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <p className="text-gray-900">{contactInfo.phone}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <p className="text-gray-900">{contactInfo.address}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Hours</label>
                    <p className="text-gray-900">{contactInfo.business_hours}</p>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={Mail}
                  title="No contact information configured"
                  description="Add your business contact details"
                  action={
                    <button
                      onClick={() => openModal('contact')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add Contact Info
                    </button>
                  }
                />
              )}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">
                {editingItem ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
              </h3>
              <button
                onClick={closeModal}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                {/* Hero Form */}
                {modalType === 'hero' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                      <textarea
                        value={formData.subtitle || ''}
                        onChange={(e) => handleInputChange('subtitle', e.target.value)}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                        <input
                          type="text"
                          value={formData.cta_text || ''}
                          onChange={(e) => handleInputChange('cta_text', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link</label>
                        <input
                          type="text"
                          value={formData.cta_link || ''}
                          onChange={(e) => handleInputChange('cta_link', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Image URL</label>
                      <input
                        type="text"
                        value={formData.background_image || ''}
                        onChange={(e) => handleInputChange('background_image', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://..."
                      />
                    </div>
                  </>
                )}

                {/* Testimonial Form */}
                {modalType === 'testimonial' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                      <input
                        type="text"
                        value={formData.role || ''}
                        onChange={(e) => handleInputChange('role', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                      <textarea
                        value={formData.content || ''}
                        onChange={(e) => handleInputChange('content', e.target.value)}
                        rows="4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                      <input
                        type="text"
                        value={formData.image || ''}
                        onChange={(e) => handleInputChange('image', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                      <select
                        value={formData.rating || 5}
                        onChange={(e) => handleInputChange('rating', parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {[1, 2, 3, 4, 5].map(num => (
                          <option key={num} value={num}>{num} Star{num > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Feature Form */}
                {modalType === 'feature' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                        <input
                          type="text"
                          value={formData.icon || ''}
                          onChange={(e) => handleInputChange('icon', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Star, Zap, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                        <select
                          value={formData.color || 'blue'}
                          onChange={(e) => handleInputChange('color', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {['blue', 'green', 'purple', 'orange', 'red', 'yellow', 'indigo', 'pink'].map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {/* FAQ Form */}
                {modalType === 'faq' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                      <input
                        type="text"
                        value={formData.question || ''}
                        onChange={(e) => handleInputChange('question', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                      <textarea
                        value={formData.answer || ''}
                        onChange={(e) => handleInputChange('answer', e.target.value)}
                        rows="4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                )}

                {/* Contact Form */}
                {modalType === 'contact' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={formData.email || ''}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input
                          type="tel"
                          value={formData.phone || ''}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                      <input
                        type="text"
                        value={formData.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours</label>
                      <input
                        type="text"
                        value={formData.business_hours || ''}
                        onChange={(e) => handleInputChange('business_hours', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Mon-Fri: 9AM-6PM"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
                        <input
                          type="url"
                          value={formData.twitter_url || ''}
                          onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn URL</label>
                        <input
                          type="url"
                          value={formData.linkedin_url || ''}
                          onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
                        <input
                          type="url"
                          value={formData.facebook_url || ''}
                          onChange={(e) => handleInputChange('facebook_url', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loadingStates.saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {loadingStates.saving ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Delete</h3>
                <p className="text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.type, deleteConfirm.id)}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard